import { useState, useEffect, Component, ReactNode } from "react";
import { Plus, Search, Filter, Edit, Trash2, UserPlus, Eye, Users, X, Building2, ArrowLeft, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SidebarProvider } from "@/components/ui/sidebar";
import AdminSidebar from "./Sidebar";
import Header from "./Header";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase/config";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";

// Error Boundary Component
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return <p className="text-sm text-destructive">Error loading dropdown. Please refresh.</p>;
    }
    return this.props.children;
  }
}

// Declare process.env types
declare global {
  namespace NodeJS {
    interface ProcessEnv {
      NODE_ENV: "development" | "production" | "test";
    }
  }
}

interface Subject {
  subjectId: string;
  name: string;
  code: string;
  department: string;
  type: "Core" | "Elective";
  assignedTeachers: number;
  teacherNames?: string[];
  description?: string;
  headOfDepartmentId?: string;
  schoolID: string;
  submittedBy: string;
  timestamp: string;
}

interface Department {
  departmentId: string;
  name: string;
  headOfDepartmentId: string;
  assignedSubjects: string[];
  schoolID: string;
  submittedBy: string;
  timestamp: string;
}

interface Teacher {
  id: string;
  firstName: string;
  lastName: string;
}

interface SubjectFormData {
  subjectName: string;
  subjectCode?: string;
  department: string;
  type: "core" | "elective";
  description?: string;
  headOfDepartmentId?: string;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
}

interface SubjectsManagementProps {
  onNavigateToForm?: () => void;
}

const SubjectRegistrationForm = ({ onBack, schoolID, submittedBy, departments, teachers }: { onBack?: () => void; schoolID: string; submittedBy: string; departments: Department[]; teachers: Teacher[] }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [teacherSearch, setTeacherSearch] = useState("");

  const form = useForm<SubjectFormData>({
    defaultValues: {
      subjectName: "",
      subjectCode: "",
      department: "",
      type: "core",
      description: "",
      headOfDepartmentId: "",
    },
  });

  const onSubmit = async (data: SubjectFormData) => {
    setIsSubmitting(true);
    try {
      const subjectsCollection = collection(db, `schools/${schoolID}/subjects`);
      const subjectId = doc(subjectsCollection).id;

      // Check for duplicate subject name or code
      const [nameSnapshot, codeSnapshot] = await Promise.all([
        getDocs(query(subjectsCollection, where("name", "==", data.subjectName))),
        data.subjectCode ? getDocs(query(subjectsCollection, where("code", "==", data.subjectCode))) : Promise.resolve({ empty: true }),
      ]);

      if (!nameSnapshot.empty) throw new Error("A subject with this name already exists.");
      if (!codeSnapshot.empty) throw new Error("A subject with this code already exists.");

      // Create subject data
      const subjectData: Subject = {
        subjectId,
        name: data.subjectName,
        code: data.subjectCode || "",
        department: data.department,
        type: data.type === "core" ? "Core" : "Elective",
        assignedTeachers: 0,
        teacherNames: [],
        schoolID,
        submittedBy,
        timestamp: new Date().toISOString(),
      };

      if (data.description) subjectData.description = data.description;
      if (data.headOfDepartmentId) subjectData.headOfDepartmentId = data.headOfDepartmentId;

      if (process.env.NODE_ENV !== "production") {
        console.log("Saving subjectData:", subjectData);
      }

      await setDoc(doc(db, `schools/${schoolID}/subjects`, subjectId), subjectData);

      toast({
        title: "Subject Registered",
        description: `${data.subjectName} added successfully.`,
        duration: 3000,
      });

      form.reset();
      if (onBack) setTimeout(() => onBack(), 1500);
    } catch (error: any) {
      console.error("Subject registration error:", error);
      toast({
        title: "Registration Failed",
        description: error.message || "Failed to register subject.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredTeachers = teachers.filter((teacher) =>
    teacher.id &&
    teacher.firstName &&
    teacher.lastName &&
    teacher.firstName.trim() !== "" &&
    teacher.lastName.trim() !== "" &&
    (teacher.firstName.toLowerCase().includes(teacherSearch.toLowerCase()) ||
     teacher.lastName.toLowerCase().includes(teacherSearch.toLowerCase()))
  );

  return (
    <div className="flex-1 max-w-full overflow-hidden">
      <div className="p-4 md:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB] dark:hover:text-gray-300 font-medium flex-shrink-0">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Register New Subject</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Add a new subject to the school curriculum
              </p>
            </div>
          </div>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Card className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
              <CardHeader>
                <CardTitle className="text-lg">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="subjectName"
                    rules={{ required: "Subject name is required" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subject Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Mathematics" {...field} className="bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]" />
                        </FormControl>
                        <FormDescription>The full name of the subject</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="subjectCode"
                    rules={{
                      pattern: {
                        value: /^[A-Z]{2,4}[0-9]{2,3}$/,
                        message: "Code format: 2-4 letters + 2-3 numbers (e.g., MATH101)",
                      },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subject Code</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., MATH101"
                            {...field}
                            onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                            className="bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
                          />
                        </FormControl>
                        <FormDescription>Unique identifier (e.g., MATH101, ENG201) - optional</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="department"
                  rules={{ required: "Department is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Department / Category *</FormLabel>
                      <ErrorBoundary>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                              <SelectValue placeholder="Select department" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                            {departments
                              .filter((dept) => dept.name && dept.name.trim() !== "")
                              .map((dept) => (
                                <SelectItem key={dept.departmentId} value={dept.name} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                                  {dept.name}
                                </SelectItem>
                              ))}
                            {departments.filter((dept) => dept.name && dept.name.trim() !== "").length === 0 && (
                              <SelectItem value="empty-departments" disabled className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                                No departments available
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </ErrorBoundary>
                      <FormDescription>Subject category or department</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="headOfDepartmentId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Head of Subject Division</FormLabel>
                      <ErrorBoundary>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                              <SelectValue placeholder="Select head of subject division" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                            <div className="p-2">
                              <Input
                                placeholder="Search teachers..."
                                value={teacherSearch}
                                onChange={(e) => setTeacherSearch(e.target.value)}
                                className="w-full h-8 bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
                              />
                            </div>
                            {filteredTeachers.map((teacher) => (
                              <SelectItem key={teacher.id} value={teacher.id} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                                {`${teacher.firstName} ${teacher.lastName}`}
                              </SelectItem>
                            ))}
                            {filteredTeachers.length === 0 && (
                              <SelectItem value="empty-teachers" disabled className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                                No teachers available
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </ErrorBoundary>
                      <FormDescription>Teacher responsible for the subject (optional)</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="type"
                  rules={{ required: "Subject type is required" }}
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>Subject Type *</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex flex-col space-y-2"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="core" id="core" />
                            <Label htmlFor="core">Core/Compulsory Subject</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="elective" id="elective" />
                            <Label htmlFor="elective">Elective Subject</Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormDescription>Is this subject mandatory or optional?</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
            <Card className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden">
              <CardHeader>
                <CardTitle className="text-lg">Additional Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Subject Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe the subject content, objectives, and learning outcomes..."
                          className="min-h-[100px] resize-none bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>Brief description of the subject (optional)</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => form.reset()}
                className="w-full sm:w-auto bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB] dark:hover:text-gray-300 font-medium flex-shrink-0"
              >
                <X className="mr-2 h-4 w-4" />
                Clear Form
              </Button>
              <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] transition-colors">
                <Save className="mr-2 h-4 w-4" />
                {isSubmitting ? "Registering..." : "Register Subject"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

const SubjectsManagement = ({ onNavigateToForm }: SubjectsManagementProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [assigningTeachers, setAssigningTeachers] = useState<Subject | null>(null);
  const [viewingSubject, setViewingSubject] = useState<Subject | null>(null);
  const [showDepartmentForm, setShowDepartmentForm] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [schoolID, setSchoolID] = useState("");
  const [submittedBy, setSubmittedBy] = useState("");

  // Fetch subjects, departments, and teachers
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;

    const fetchData = async () => {
      try {
        // Fetch subjects
        const subjectsCollection = collection(db, `schools/${schoolID}/subjects`);
        const subjectsSnapshot = await getDocs(subjectsCollection);
        const subjectsData = subjectsSnapshot.docs.map((doc) => ({
          subjectId: doc.id,
          ...doc.data(),
        })) as Subject[];

        if (process.env.NODE_ENV !== "production") {
          console.log("Fetched subjects:", subjectsData);
        }

        setSubjects(subjectsData);

        // Fetch departments
        const departmentsCollection = collection(db, `schools/${schoolID}/departments`);
        const departmentsSnapshot = await getDocs(departmentsCollection);
        const departmentsData = departmentsSnapshot.docs
          .map((doc) => ({
            ...(doc.data() as Department),
            departmentId: doc.id,
          }))
          .filter((dept) => dept.name && dept.name.trim() !== "");
        setDepartments(departmentsData);

        // Fetch teachers
        const teachersCollection = collection(db, `schools/${schoolID}/teachers`);
        const teachersSnapshot = await getDocs(teachersCollection);
        const teachersData = teachersSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            firstName: doc.data().firstName,
            lastName: doc.data().lastName,
          }))
          .filter((teacher) => teacher.id && teacher.firstName && teacher.lastName && teacher.firstName.trim() !== "" && teacher.lastName.trim() !== "") as Teacher[];
        setTeachers(teachersData);

        if (process.env.NODE_ENV !== "production") {
          console.log("Fetched teachers:", teachersData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Data Fetch Failed",
          description: "Unable to load subjects, departments, or teachers.",
          variant: "destructive",
          duration: 5000,
        });
      }
    };

    fetchData();
  }, [schoolID, hasValidClaims, toast]);

  // Authentication and claims verification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        // Don't redirect here - let App.tsx global routing handle it
        // This prevents incorrect redirects during network errors
        console.log("SubjectsManagement: No user found, deferring to App.tsx routing");
        setIsAuthLoading(false);
        return;
      }

      setSubmittedBy(user.uid);
      try {
        const token = await getIdTokenResult(user, true);
        const claims = token.claims as CustomClaims;
        const affiliations: Affiliation[] = claims.affiliations || [];
        const defaultAff: Affiliation | undefined = claims.defaultAffiliation;
        const claimSchoolID: string | undefined = claims.schoolID;

        if (process.env.NODE_ENV !== "production") {
          console.log("Claims:", { affiliations, defaultAff, claimSchoolID });
        }

        const adminAff =
          defaultAff?.role === "admin" && defaultAff.schoolID
            ? defaultAff
            : affiliations.find((aff) => aff.role === "admin" && aff.schoolID);

        if (adminAff && adminAff.schoolID === claimSchoolID) {
          setSchoolID(adminAff.schoolID!);
          setHasValidClaims(true);
        } else {
          if (process.env.NODE_ENV !== "production") console.log("Retrying claims after 10s...");
          await new Promise((resolve) => setTimeout(resolve, 10000));
          const retryToken = await getIdTokenResult(user, true);
          const retryClaims = retryToken.claims as CustomClaims;
          const retryAffiliations: Affiliation[] = retryClaims.affiliations || [];
          const retryDefaultAff: Affiliation | undefined = retryClaims.defaultAffiliation;
          const retrySchoolID: string | undefined = retryClaims.schoolID;

          const retryAdminAff =
            retryDefaultAff?.role === "admin" && retryDefaultAff.schoolID
              ? retryDefaultAff
              : retryAffiliations.find((aff) => aff.role === "admin" && aff.schoolID);

          if (retryAdminAff && retryAdminAff.schoolID === retrySchoolID) {
            setSchoolID(retryAdminAff.schoolID!);
            setHasValidClaims(true);
          } else {
            throw new Error("Invalid admin claims");
          }
        }
      } catch (error: any) {
        console.error("Claims validation error:", error);
        // Check if it's a network error
        const isNetworkError = error?.code === 'auth/network-request-failed' ||
                              error?.message?.includes('ERR_NAME_NOT_RESOLVED') ||
                              error?.message?.includes('network-request-failed') ||
                              error?.message?.includes('network');
        
        if (isNetworkError) {
          // Don't logout on network errors, just show a warning
          toast({
            title: "Network Error",
            description: "Unable to verify permissions due to network issues. Please check your connection.",
            variant: "destructive",
            duration: 5000,
          });
        } else {
          // Only logout for non-network errors
          toast({
            title: "Missing Permissions",
            description: "You lack permissions to manage subjects.",
            variant: "destructive",
            duration: 5000,
          });
          navigate("/login-page");
        }
      }
      setIsAuthLoading(false);
    });

    return () => {
      if (process.env.NODE_ENV !== "production") console.log("Unsubscribing auth");
      unsubscribe();
    };
  }, [toast, navigate]);

  const filteredSubjects = subjects.filter((subject) => {
    const matchesSearch =
      subject.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subject.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = departmentFilter === "all" || subject.department === departmentFilter;
    const matchesType = typeFilter === "all" || subject.type === typeFilter;
    return matchesSearch && matchesDepartment && matchesType;
  });

  const handleDelete = async (subjectId: string) => {
    try {
      await deleteDoc(doc(db, `schools/${schoolID}/subjects`, subjectId));
      setSubjects((prev) => prev.filter((subject) => subject.subjectId !== subjectId));
      toast({
        title: "Subject Deleted",
        description: "The subject was successfully deleted.",
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Delete error:", error);
      toast({
        title: "Deletion Failed",
        description: error.message || "Unable to delete subject.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleEdit = (subject: Subject) => {
    setEditingSubject(subject);
  };

  const handleSaveEdit = async (updatedSubject: Subject) => {
    try {
      await setDoc(doc(db, `schools/${schoolID}/subjects`, updatedSubject.subjectId), {
        ...updatedSubject,
        timestamp: new Date().toISOString(),
      });
      setSubjects((prev) => prev.map((s) => (s.subjectId === updatedSubject.subjectId ? updatedSubject : s)));
      setEditingSubject(null);
      toast({
        title: "Subject Updated",
        description: `${updatedSubject.name} updated successfully.`,
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Update error:", error);
      toast({
        title: "Update Failed",
        description: error.message || "Unable to update subject.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleAssignTeachers = (subject: Subject) => {
    setAssigningTeachers(subject);
  };

  const handleSaveTeacherAssignment = async (subjectId: string, teacherNames: string[]) => {
    try {
      await setDoc(
        doc(db, `schools/${schoolID}/subjects`, subjectId),
        {
          teacherNames,
          assignedTeachers: teacherNames.length,
          timestamp: new Date().toISOString(),
        },
        { merge: true }
      );
      setSubjects((prev) =>
        prev.map((s) =>
          s.subjectId === subjectId
            ? { ...s, teacherNames, assignedTeachers: teacherNames.length }
            : s
        )
      );
      setAssigningTeachers(null);
      toast({
        title: "Teachers Assigned",
        description: "Teacher assignments updated successfully.",
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Assignment error:", error);
      toast({
        title: "Assignment Failed",
        description: error.message || "Unable to assign teachers.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleViewDetails = (subject: Subject) => {
    setViewingSubject(subject);
  };

  const handleNavigateToForm = () => {
    setShowRegistrationForm(true);
    if (onNavigateToForm) onNavigateToForm();
  };

  const handleBackFromForm = () => {
    setShowRegistrationForm(false);
  };

  if (isAuthLoading || !hasValidClaims) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-gray-50 dark:bg-gray-900">
          <AdminSidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 min-h-0 max-w-[100vw]">
            <Header />
            <main className="flex-grow p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .font-roboto {
            font-family: 'Roboto', sans-serif;
          }
        `}
      </style>
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto">
          <AdminSidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full">
            <Header />
            <main className="flex-1 overflow-y-auto min-h-0 p-2 sm:p-4 md:p-6 lg:p-8 pt-3 sm:pt-4 md:pt-6">
              {showRegistrationForm ? (
                <SubjectRegistrationForm
                  onBack={handleBackFromForm}
                  schoolID={schoolID}
                  submittedBy={submittedBy}
                  departments={departments}
                  teachers={teachers}
                />
              ) : (
                <div className="flex-1 space-y-3 w-full overflow-hidden">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold tracking-tight truncate">Subjects Management</h2>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1 truncate">
                        Manage school subjects and their assignments
                      </p>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Button
                        onClick={handleNavigateToForm}
                        className="flex-1 sm:flex-initial min-w-0 bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] transition-colors"
                        size="sm"
                      >
                        <Plus className="mr-1 h-4 w-4 flex-shrink-0" />
                        <span className="hidden sm:inline truncate">Register Subject</span>
                        <span className="sm:hidden truncate">Subject</span>
                      </Button>
                      <Button
                        onClick={() => setShowDepartmentForm(true)}
                        className="flex-1 sm:flex-initial min-w-0 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB] dark:hover:text-gray-300 font-medium flex-shrink-0"
                        size="sm"
                        variant="outline"
                      >
                        <Building2 className="mr-1 h-4 w-4 flex-shrink-0" />
                        <span className="hidden sm:inline truncate">Register Dept</span>
                        <span className="sm:hidden truncate">Dept</span>
                      </Button>
                    </div>
                  </div>
                  <div className="grid gap-2 sm:gap-3 grid-cols-2 lg:grid-cols-4 w-full m-1">
                    <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] rounded-xl overflow-hidden">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-xs sm:text-sm font-medium truncate">Total Subjects</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-lg sm:text-2xl font-bold">{subjects.length}</div>
                      </CardContent>
                    </Card>
                    <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] rounded-xl overflow-hidden">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-xs sm:text-sm font-medium truncate">Core Subjects</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-lg sm:text-2xl font-bold">{subjects.filter((s) => s.type === "Core").length}</div>
                      </CardContent>
                    </Card>
                    <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] rounded-xl overflow-hidden">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-xs sm:text-sm font-medium truncate">Elective Subjects</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-lg sm:text-2xl font-bold">{subjects.filter((s) => s.type === "Elective").length}</div>
                      </CardContent>
                    </Card>
                    <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] rounded-xl overflow-hidden">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-xs sm:text-sm font-medium truncate">Departments</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-lg sm:text-2xl font-bold">{departments.length}</div>
                      </CardContent>
                    </Card>
                  </div>
                  <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] rounded-xl overflow-hidden">
                    <CardHeader>
                      <CardTitle className="text-lg">Search & Filters</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                        <Input
                          placeholder="Search by subject name or code..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 text-sm bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
                        />
                      </div>
                      <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full mt-2">
                        <ErrorBoundary>
                          <Select value={departmentFilter} onValueChange={(value) => setDepartmentFilter(value ?? "all")}>
                            <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                              <SelectValue placeholder="All Departments" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                              <SelectItem value="all" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">All Departments</SelectItem>
                              {departments
                                .filter((dept) => dept.name && dept.name.trim() !== "")
                                .map((dept) => (
                                  <SelectItem key={dept.departmentId} value={dept.name} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                                    {dept.name}
                                  </SelectItem>
                                ))}
                              {departments.filter((dept) => dept.name && dept.name.trim() !== "").length === 0 && (
                                <SelectItem value="empty-departments" disabled className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                                  No departments available
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </ErrorBoundary>
                        <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value ?? "all")}>
                          <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                            <SelectValue placeholder="All Types" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                            <SelectItem value="all" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">All Types</SelectItem>
                            <SelectItem value="Core" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Core</SelectItem>
                            <SelectItem value="Elective" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Elective</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSearchTerm("");
                            setDepartmentFilter("all");
                            setTypeFilter("all");
                          }}
                          className="w-full bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB] dark:hover:text-gray-300 font-medium flex-shrink-0"
                          size="sm"
                        >
                          <Filter className="mr-2 h-4 w-4" />
                          <span className="hidden sm:inline">Clear Filters</span>
                          <span className="sm:hidden">Clear</span>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] rounded-xl overflow-hidden">
                    <CardHeader>
                      <CardTitle className="text-lg">Subjects ({filteredSubjects.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-md border overflow-hidden w-full border-[#F1F5F9] dark:border-[#1E293B]">
                        <div className="overflow-x-auto">
                          <Table className="w-full">
                            <TableHeader>
                              <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                                <TableHead className="w-[120px] max-w-[120px]">Subject</TableHead>
                                <TableHead className="w-[80px] max-w-[80px] hidden sm:table-cell">Code</TableHead>
                                <TableHead className="w-[100px] max-w-[100px] hidden md:table-cell">Department</TableHead>
                                <TableHead className="w-[70px] max-w-[70px]">Type</TableHead>
                                <TableHead className="w-[80px] max-w-[80px] hidden lg:table-cell">Teachers</TableHead>
                                <TableHead className="w-[100px] max-w-[100px]">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredSubjects.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={7} className="text-center py-4 text-muted-foreground">
                                    No subjects found matching your criteria
                                  </TableCell>
                                </TableRow>
                              ) : (
                                filteredSubjects.map((subject) => (
                                  <TableRow key={subject.subjectId} className="border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929]">
                                    <TableCell className="max-w-[120px] font-medium p-1 sm:p-2">
                                      <div className="space-y-1">
                                        <div className="font-medium text-sm truncate" title={subject.name}>{subject.name}</div>
                                        <div className="text-xs text-muted-foreground sm:hidden truncate">{subject.code}</div>
                                        <div className="text-xs text-muted-foreground md:hidden truncate">{subject.department}</div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="hidden sm:table-cell max-w-[80px] p-1 sm:p-2">
                                      <code className="text-xs bg-muted px-1 py-0.5 rounded truncate block" title={subject.code}>{subject.code}</code>
                                    </TableCell>
                                    <TableCell className="hidden md:table-cell max-w-[100px] p-1 sm:p-2">
                                      <span className="text-sm truncate block" title={subject.department}>{subject.department}</span>
                                    </TableCell>
                                    <TableCell className="max-w-[70px] p-1 sm:p-2">
                                      <Badge variant={subject.type === "Core" ? "default" : "secondary"} className="text-white text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E]">
                                        {subject.type === "Core" ? "C" : "E"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="hidden lg:table-cell max-w-[80px] p-1 sm:p-2">
                                      <div className="flex items-center gap-1">
                                        <Users className="h-3 w-3 text-muted-foreground" />
                                        <span className="text-sm">{subject.assignedTeachers}</span>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-right p-1 sm:p-2">
                                      <div className="flex gap-0.5 justify-end">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleEdit(subject)}
                                          className="h-7 w-7 p-0 my-2"
                                          title="Edit"
                                        >
                                          <Edit className="h-3 w-3" />
                                          <span className="sr-only">Edit</span>
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleAssignTeachers(subject)}
                                          className="h-7 w-7 p-0 my-2"
                                          title="Assign Teachers"
                                        >
                                          <UserPlus className="h-3 w-3" />
                                          <span className="sr-only">Assign</span>
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleViewDetails(subject)}
                                          className="h-7 w-7 p-0 my-2"
                                          title="View Details"
                                        >
                                          <Eye className="h-3 w-3" />
                                          <span className="sr-only">View</span>
                                        </Button>
                                        <AlertDialog>
                                          <AlertDialogTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="text-destructive h-7 w-7 p-0 my-2 hover:text-destructive"
                                              title="Delete"
                                            >
                                              <Trash2 className="h-3 w-3" />
                                              <span className="sr-only">Delete</span>
                                            </Button>
                                          </AlertDialogTrigger>
                                          <AlertDialogContent className="max-w-[90vw] sm:max-w-lg mx-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] max-h-[90vh] overflow-y-auto">
                                            <AlertDialogHeader>
                                              <AlertDialogTitle>Delete Subject</AlertDialogTitle>
                                              <AlertDialogDescription>
                                                Are you sure you want to delete {subject.name}? This action cannot be undone.
                                              </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                              <AlertDialogCancel className="w-full sm:w-auto bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]">Cancel</AlertDialogCancel>
                                              <AlertDialogAction
                                                onClick={() => handleDelete(subject.subjectId)}
                                                className="w-full sm:w-auto bg-[#EF4444] dark:bg-[#F87171] hover:bg-[#DC2626] dark:hover:bg-[#EF4444] text-white"
                                              >
                                                Delete
                                              </AlertDialogAction>
                                            </AlertDialogFooter>
                                          </AlertDialogContent>
                                        </AlertDialog>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Dialog open={!!editingSubject} onOpenChange={(open) => !open && setEditingSubject(null)}>
                    <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                      <DialogHeader>
                        <DialogTitle>Edit Subject</DialogTitle>
                        <DialogDescription>Update the subject information below.</DialogDescription>
                      </DialogHeader>
                      {editingSubject && (
                        <EditSubjectForm
                          subject={editingSubject}
                          onSave={handleSaveEdit}
                          onCancel={() => setEditingSubject(null)}
                          departments={departments}
                          teachers={teachers}
                        />
                      )}
                    </DialogContent>
                  </Dialog>
                  <Dialog open={!!viewingSubject} onOpenChange={(open) => !open && setViewingSubject(null)}>
                    <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                      <DialogHeader>
                        <DialogTitle>Subject Details</DialogTitle>
                        <DialogDescription>Complete information about {viewingSubject?.name}</DialogDescription>
                      </DialogHeader>
                      {viewingSubject && <SubjectDetailsView subject={viewingSubject} teachers={teachers} />}
                    </DialogContent>
                  </Dialog>
                  <Dialog open={!!assigningTeachers} onOpenChange={(open) => !open && setAssigningTeachers(null)}>
                    <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                      <DialogHeader>
                        <DialogTitle>Assign Teachers</DialogTitle>
                        <DialogDescription>Assign teachers to {assigningTeachers?.name}</DialogDescription>
                      </DialogHeader>
                      {assigningTeachers && (
                        <TeacherAssignmentForm
                          subject={assigningTeachers}
                          availableTeachers={teachers.map((t) => `${t.firstName} ${t.lastName}`)}
                          onSave={handleSaveTeacherAssignment}
                          onCancel={() => setAssigningTeachers(null)}
                        />
                      )}
                    </DialogContent>
                  </Dialog>
                  <Dialog open={showDepartmentForm} onOpenChange={setShowDepartmentForm}>
                    <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                      <DialogHeader>
                        <DialogTitle>Register New Department</DialogTitle>
                        <DialogDescription>Create a new department and assign subjects to it.</DialogDescription>
                      </DialogHeader>
                      <DepartmentRegistrationForm
                        subjects={subjects}
                        availableTeachers={teachers}
                        onSave={(departmentData) => {
                          onSaveDepartment(departmentData, schoolID, submittedBy);
                          setShowDepartmentForm(false);
                        }}
                        onCancel={() => setShowDepartmentForm(false)}
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </main>
          </div>
        </div>
      </SidebarProvider>
    </>
  );

  async function onSaveDepartment(departmentData: Department, schoolID: string, submittedBy: string) {
    try {
      const departmentsCollection = collection(db, `schools/${schoolID}/departments`);
      const departmentId = doc(departmentsCollection).id;

      // Check for duplicate department name
      const nameSnapshot = await getDocs(query(departmentsCollection, where("name", "==", departmentData.name)));
      if (!nameSnapshot.empty) throw new Error("A department with this name already exists.");

      const departmentRecord: Department = {
        departmentId,
        name: departmentData.name,
        headOfDepartmentId: departmentData.headOfDepartmentId,
        assignedSubjects: departmentData.assignedSubjects,
        schoolID,
        submittedBy,
        timestamp: new Date().toISOString(),
      };

      await setDoc(doc(db, `schools/${schoolID}/departments`, departmentId), departmentRecord);

      // Update departments state
      setDepartments((prev) => [...prev, departmentRecord]);

      toast({
        title: "Department Registered",
        description: `${departmentData.name} added successfully.`,
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Department registration error:", error);
      toast({
        title: "Registration Failed",
        description: error.message || "Failed to register department.",
        variant: "destructive",
        duration: 5000,
      });
    }
  }
};

const EditSubjectForm = ({ subject, onSave, onCancel, departments, teachers }: { subject: Subject; onSave: (subject: Subject) => void; onCancel: () => void; departments: Department[]; teachers: Teacher[] }) => {
  const [formData, setFormData] = useState(subject);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4">
        <div>
          <Label htmlFor="name">Subject Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            className="text-sm bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
          />
        </div>
        <div>
          <Label htmlFor="code">Subject Code</Label>
          <Input
            id="code"
            value={formData.code}
            onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
            className="text-sm bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="department">Department</Label>
            <ErrorBoundary>
              <Select
                value={formData.department}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, department: value }))}
              >
                <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                  {departments
                    .filter((dept) => dept.name && dept.name.trim() !== "")
                    .map((dept) => (
                      <SelectItem key={dept.departmentId} value={dept.name} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                        {dept.name}
                      </SelectItem>
                    ))}
                  {departments.filter((dept) => dept.name && dept.name.trim() !== "").length === 0 && (
                    <SelectItem value="empty-departments" disabled className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                      No departments available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </ErrorBoundary>
          </div>
          <div>
            <Label htmlFor="type">Type</Label>
            <Select
              value={formData.type}
              onValueChange={(value: "Core" | "Elective") => setFormData((prev) => ({ ...prev, type: value }))}
            >
              <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                <SelectItem value="Core" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Core</SelectItem>
                <SelectItem value="Elective" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Elective</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="headOfDepartmentId">Head of Subject Division</Label>
          <ErrorBoundary>
            <Select
              value={formData.headOfDepartmentId}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, headOfDepartmentId: value }))}
            >
              <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                <SelectValue placeholder="Select head of subject division" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                {teachers
                  .filter((teacher) => teacher.id && teacher.firstName && teacher.lastName && teacher.firstName.trim() !== "" && teacher.lastName.trim() !== "")
                  .map((teacher) => (
                    <SelectItem key={teacher.id} value={teacher.id} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                      {`${teacher.firstName} ${teacher.lastName}`}
                    </SelectItem>
                  ))}
                {teachers.filter((teacher) => teacher.id && teacher.firstName && teacher.lastName && teacher.firstName.trim() !== "" && teacher.lastName.trim() !== "").length === 0 && (
                  <SelectItem value="empty-teachers" disabled className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                    No teachers available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </ErrorBoundary>
        </div>
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description || ""}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            className="text-sm min-h-[60px] bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
          />
        </div>
      </div>
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="w-full sm:w-auto bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]">
          Cancel
        </Button>
        <Button type="submit" className="w-full sm:w-auto bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] transition-colors">
          Save Changes
        </Button>
      </DialogFooter>
    </form>
  );
};

const SubjectDetailsView = ({ subject, teachers }: { subject: Subject; teachers: Teacher[] }) => {
  const getTeacherName = (teacherId?: string) => {
    if (!teacherId) return "Not assigned";
    const teacher = teachers.find((t) => t.id === teacherId);
    return teacher ? `${teacher.firstName} ${teacher.lastName}` : "Unknown teacher";
  };

  return (
    <div className="space-y-4 m-2">
      <div className="grid gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Subject Name</Label>
            <p className="text-sm font-medium">{subject.name}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Subject Code</Label>
            <p className="text-sm">
              <code className="bg-muted px-2 py-1 rounded text-xs">{subject.code}</code>
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Department</Label>
            <p className="text-sm">{subject.department}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Head of Subject Division</Label>
            <p className="text-sm">{getTeacherName(subject.headOfDepartmentId)}</p>
          </div>
        </div>
        <div>
          <Label className="text-sm font-medium text-muted-foreground">Type</Label>
          <p>
            <Badge variant={subject.type === "Core" ? "default" : "secondary"} className="text-white text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E]">{subject.type}</Badge>
          </p>
        </div>
        <div>
          <Label className="text-sm font-medium text-muted-foreground">Assigned Teachers ({subject.assignedTeachers})</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {subject.teacherNames?.map((teacher, index) => (
              <Badge key={index} variant="outline" className=" text-white text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E]">
                {teacher}
              </Badge>
            )) || <span className="text-sm text-muted-foreground">No teachers assigned</span>}
          </div>
        </div>
        {subject.description && (
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Description</Label>
            <p className="text-sm mt-1">{subject.description}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const TeacherAssignmentForm = ({
  subject,
  availableTeachers,
  onSave,
  onCancel,
}: {
  subject: Subject;
  availableTeachers: string[];
  onSave: (subjectId: string, teacherNames: string[]) => void;
  onCancel: () => void;
}) => {
  const [selectedTeachers, setSelectedTeachers] = useState<string[]>(subject.teacherNames || []);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredTeachers = availableTeachers.filter((teacher) =>
    teacher.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleTeacherToggle = (teacher: string) => {
    setSelectedTeachers((prev) =>
      prev.includes(teacher) ? prev.filter((t) => t !== teacher) : [...prev, teacher]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(subject.subjectId, selectedTeachers);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 m-2">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Label className="text-sm font-medium">Available Teachers</Label>
          <div className="flex-1 relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search teachers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 text-sm h-9 bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
            />
          </div>
        </div>
        <div className="mt-2 space-y-2 max-h-[40vh] overflow-y-auto border rounded-md p-3 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
          {filteredTeachers.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-4">
              {searchTerm ? "No teachers found matching your search" : "No teachers available"}
            </div>
          ) : (
            filteredTeachers.map((teacher) => (
              <div key={teacher} className="flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-[#131E31] p-1 rounded">
                <input
                  type="checkbox"
                  id={teacher}
                  checked={selectedTeachers.includes(teacher)}
                  onChange={() => handleTeacherToggle(teacher)}
                  className="rounded border-input"
                />
                <Label htmlFor={teacher} className="text-sm cursor-pointer flex-1">
                  {teacher}
                </Label>
              </div>
            ))
          )}
        </div>
      </div>
      <div>
        <Label className="text-sm font-medium text-muted-foreground">
          Selected: {selectedTeachers.length} teacher(s)
        </Label>
        <div className="flex flex-wrap gap-1 mt-1">
          {selectedTeachers.map((teacher) => (
            <Badge key={teacher} variant="outline" className=" text-white text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E]">
              {teacher}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-1 h-auto p-0 hover:bg-transparent"
                onClick={() => handleTeacherToggle(teacher)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      </div>
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="w-full sm:w-auto bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]">
          Cancel
        </Button>
        <Button type="submit" className="w-full sm:w-auto bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] transition-colors">
          Assign Teachers
        </Button>
      </DialogFooter>
    </form>
  );
};

const DepartmentRegistrationForm = ({
  subjects,
  availableTeachers,
  onSave,
  onCancel,
}: {
  subjects: Subject[];
  availableTeachers: Teacher[];
  onSave: (department: Department) => void;
  onCancel: () => void;
}) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<Department>({
    departmentId: "",
    name: "",
    headOfDepartmentId: "",
    assignedSubjects: [],
    schoolID: "",
    submittedBy: "",
    timestamp: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubjectToggle = (subjectId: string) => {
    setFormData((prev) => ({
      ...prev,
      assignedSubjects: prev.assignedSubjects.includes(subjectId)
        ? prev.assignedSubjects.filter((id) => id !== subjectId)
        : [...prev.assignedSubjects, subjectId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.headOfDepartmentId) {
      toast({
        title: "Missing Information",
        description: "Department name and head of department are required.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      onSave(formData);
    } catch (error: any) {
      console.error("Department registration error:", error);
      toast({
        title: "Registration Failed",
        description: error.message || "Failed to register department.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 m-2">
      <div className="grid gap-4">
        <div>
          <Label htmlFor="dept-name">Department Name *</Label>
          <Input
            id="dept-name"
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Computer Science"
            required
            className="text-sm bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
          />
        </div>
        <div>
          <Label htmlFor="head-of-dept">Head of Department *</Label>
          <ErrorBoundary>
            <Select
              value={formData.headOfDepartmentId}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, headOfDepartmentId: value }))}
            >
              <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                <SelectValue placeholder="Select department head" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                {availableTeachers
                  .filter((teacher) => teacher.id && teacher.firstName && teacher.lastName && teacher.firstName.trim() !== "" && teacher.lastName.trim() !== "")
                  .map((teacher) => (
                    <SelectItem key={teacher.id} value={teacher.id} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                      {`${teacher.firstName} ${teacher.lastName}`}
                    </SelectItem>
                  ))}
                {availableTeachers.filter((teacher) => teacher.id && teacher.firstName && teacher.lastName && teacher.firstName.trim() !== "" && teacher.lastName.trim() !== "").length === 0 && (
                  <SelectItem value="empty-teachers" disabled className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                    No teachers available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </ErrorBoundary>
        </div>
        <div>
          <Label className="text-sm font-medium">Department Subjects</Label>
          <div className="mt-2 space-y-2 max-h-[30vh] overflow-y-auto border rounded-md p-3 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
            {subjects.map((subject) => (
              <div key={subject.subjectId} className="flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-[#131E31] p-1 rounded cursor-pointer">
                <input
                  type="checkbox"
                  id={`subject-${subject.subjectId}-${subject.code}`}
                  title={`Select ${subject.name}`}
                  placeholder={`Select ${subject.name}`}
                  checked={formData.assignedSubjects.includes(subject.subjectId)}
                  onChange={() => handleSubjectToggle(subject.subjectId)}
                  className="rounded border-input"
                />
                <Label htmlFor={`subject-${subject.subjectId}`} className="text-sm cursor-pointer flex-1">
                  <div className="flex flex-col">
                    <span className="font-medium">{subject.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {subject.code} • {subject.type}
                    </span>
                  </div>
                </Label>
              </div>
            ))}
          </div>
          {formData.assignedSubjects.length > 0 && (
            <div className="mt-2">
              <Label className="text-sm text-muted-foreground">
                Selected: {formData.assignedSubjects.length} subject(s)
              </Label>
            </div>
          )}
        </div>
      </div>
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="w-full sm:w-auto bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]">
          Cancel
        </Button>
        <Button
          type="submit"
          className="w-full sm:w-auto bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] transition-colors"
          disabled={isSubmitting || !formData.name || !formData.headOfDepartmentId}
        >
          <Save className="mr-2 h-4 w-4" />
          {isSubmitting ? "Registering..." : "Register Department"}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default SubjectsManagement;